import { PreprocessUploadResult } from "components/modules/UploadPreprocessing/lib/types/PreprocessUploadResult";
import { popToastError } from "lib/actions/toast";
import { registerAssetUpload } from "lib/actions/assets/registerAssetUpload";
import { useAssetUploads } from "lib/hooks/assets/useAssetUploads";
import { useCurrentProject, useCurrentProjectId } from "lib/hooks/useProject";
import { useUploadPreprocessor } from "../../../UploadPreprocessing/UploadPreprocessorContext";
import { getFileExtension, pointcloudFileExtensions } from "lib/actions/files";
import { useFlags } from "launchdarkly-react-client-sdk";
import { useWorkspaceSubscription } from "lib/hooks/useWorkspaceSubscription";
import { SubscriptionBillingState } from "lib/types/subscriptions/SubscriptionBillingState";

export function useProjectFileUploadStarter() {
  const projectId = useCurrentProjectId();
  const { project } = useCurrentProject();
  const { subscriptionStatus } = useWorkspaceSubscription(
    project?.workspace.id,
  );
  const { featurePointcloudUploadsRestrict: hasPointcloudUploadRestriction } =
    useFlags();
  const uploads = useAssetUploads(projectId);
  const preprocessor = useUploadPreprocessor();

  async function startFiles(files: File[]) {
    if (projectId == null || !uploads || !preprocessor) {
      console.error("Unable to start upload, a project must be open.");
      return;
    }

    const isFreeTier =
      subscriptionStatus?.billingState === SubscriptionBillingState.None;

    const filesAllowedOnTier =
      isFreeTier && hasPointcloudUploadRestriction
        ? files.filter(
            (f) => !pointcloudFileExtensions.has(getFileExtension(f.name)),
          )
        : files;

    if (filesAllowedOnTier.length < files.length) {
      popToastError(
        "Uploading point clouds is not available on free tier projects",
      );
    }

    const preprocessed = (
      await Promise.allSettled(
        filesAllowedOnTier.map((file) => preprocessor.preprocessUpload(file)),
      )
    )
      .filter((r) => r.status === "fulfilled")
      .map((r: PromiseFulfilledResult<PreprocessUploadResult>) => r.value);

    const supported = preprocessed.filter((r) => r.isSupported);
    const unsupportedCount = preprocessed.length - supported.length;
    if (unsupportedCount !== 0) {
      if (supported.length < 1)
        popToastError(
          unsupportedCount > 1
            ? "These files are not supported. "
            : "This file is not supported. ",
        );
      else
        popToastError(
          `${unsupportedCount} file(s) are not supported. We have skipped over them.`,
        );
    }

    if (
      preprocessed.some(
        (preprocessResult) =>
          !preprocessResult.isValid && preprocessResult.isSupported,
      ) &&
      filesAllowedOnTier.length > 1
    ) {
      // As per TACO-2482, bulk uploads are treated as a single transaction for now.
      // If one file has an issue, it cancels the whole upload.
      popToastError("Invalid files detected. Whole bulk upload aborted.");
      return;
    }

    await Promise.all(
      supported
        .filter((r) => r.isValid)
        .map(({ preprocessedFile, claimedAssetType }) =>
          uploads.uploadFile(preprocessedFile, () =>
            registerAssetUpload(
              projectId,
              preprocessedFile.name,
              preprocessedFile.size,
              claimedAssetType,
            ),
          ),
        ),
    );
  }

  return {
    startFiles,
  };
}
